import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Input,
  Label,
  Button,
  Badge,
  Alert,
  Spinner
} from 'reactstrap';

import BreadCrumb from '../../Components/Common/BreadCrumb';
import TableContainer from '../../Components/Common/TableContainer';
import { getEmployeesList } from '../../utils/employees';
import { ToastContainer, toast } from 'react-toastify';

const EmployeeSearch = () => {
  // State management
  const [employees, setEmployees] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  // User role check
  const [userRole, setUserRole] = useState('');
  const [hasAccess, setHasAccess] = useState(false);

  // Check user role and access permissions
  useEffect(() => {
    try {
      const userData = sessionStorage.getItem("user");
      if (userData) {
        const user = JSON.parse(userData);
        setUserRole(user.role);
        // Only superadmin and employee3 can search employees
        setHasAccess(user.role === 'superadmin' || user.role === 'employee3');
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
      setHasAccess(false);
    }
  }, []);

  // Notification functions
  const successNotify = (message) => toast(message, {
    position: "top-center",
    hideProgressBar: true,
    closeOnClick: false,
    className: 'bg-success text-white'
  });

  const errorNotify = (message) => toast(message, {
    position: "top-center",
    hideProgressBar: true,
    closeOnClick: false,
    className: 'bg-danger text-white'
  });

  // Load initial data
  const loadInitialData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getEmployeesList({
        page: 1,
        per_page: pageSize
      });
      
      if (response && response.data && (response.data.success || response.status === 200)) {
        const employeesData = response.data.data || response.data.employees || [];
        const totalCount = response.data.total || response.data.count || employeesData.length;
        
        setEmployees(employeesData);
        setTotalResults(totalCount);
        setHasSearched(true);
        setCurrentPage(1);
        
        successNotify(`Loaded ${employeesData.length} employees`);
      } else {
        throw new Error(response.data?.message || response.message || 'Failed to fetch employees');
      }
    } catch (err) {
      console.error('Load error:', err);
      setError(err.message || 'Failed to load employees');
      setEmployees([]);
      setTotalResults(0);
      errorNotify(err.message || 'Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  // Handle page change from TableContainer
  const handlePageChange = async (params) => {
    try {
      const page = params?.pageIndex || 1;
      const pageSize = params?.pageSize || 10;
      const searchTerm = params?.searchTerm || searchQuery;
      
      console.log('handlePageChange called with params:', params);
      console.log('Using search term:', searchTerm);
      
      setCurrentPage(page);
      setPageSize(pageSize);
      
      const requestData = {
        page: page,
        per_page: pageSize
      };
      
      // Add search term if provided
      if (searchTerm && searchTerm.trim()) {
        requestData.ilike = searchTerm.trim();
      }
      
      console.log('handlePageChange request data:', requestData);
      
      const response = await getEmployeesList(requestData);
      
      if (response && response.data && (response.data.success || response.status === 200)) {
        const employeesData = response.data.data || response.data.employees || [];
        const totalCount = response.data.total || response.data.count || employeesData.length;
        
        setEmployees(employeesData);
        setTotalResults(totalCount);
        setHasSearched(true);
      }
    } catch (error) {
      console.error('Error in handlePageChange:', error);
      setError(error.message || 'Failed to fetch employees');
    }
  };



  // Table columns
  const columns = useMemo(
    () => [
      {
        header: "Employee",
        accessorKey: "first_name",
        enableColumnFilter: false,
        cell: (cellProps) => {
          const employee = cellProps.row.original;
          return (
            <div className="d-flex align-items-center">
              <div className="avatar-xs me-3">
                <div className="avatar-title rounded-circle bg-soft-primary text-primary">
                  {employee.first_name?.charAt(0)}{employee.last_name?.charAt(0)}
                </div>
              </div>
              <div>
                <h6 className="mb-0">{employee.first_name} {employee.last_name}</h6>
                <small className="text-muted">{employee.email}</small>
              </div>
            </div>
          );
        },
      },
      {
        header: "NFC ID",
        accessorKey: "nfc_tag.uid",
        enableColumnFilter: false,
        cell: (cellProps) => {
          const nfcId = cellProps.getValue();
          return nfcId || <span className="text-muted">-</span>;
        },
      },
      {
        header: "Company",
        accessorKey: "company.oem_brandname",
        enableColumnFilter: false,
        cell: (cellProps) => {
          const companyName = cellProps.getValue();
          return companyName || <span className="text-muted">-</span>;
        },
      },
      {
        header: "Department",
        accessorKey: "department",
        enableColumnFilter: false,
        cell: (cellProps) => {
          const department = cellProps.getValue();
          return department || <span className="text-muted">-</span>;
        },
      },
      {
        header: "Position",
        accessorKey: "position",
        enableColumnFilter: false,
        cell: (cellProps) => {
          const position = cellProps.getValue();
          return position || <span className="text-muted">-</span>;
        },
      },
      {
        header: "Role",
        accessorKey: "role",
        enableColumnFilter: false,
        cell: (cellProps) => {
          const role = cellProps.getValue();
          const roleColors = {
            employee1: "primary",
            employee2: "success",
            employee3: "warning",
            superadmin: "danger"
          };
          return (
            <Badge color={roleColors[role] || "secondary"} className="text-uppercase">
              {role}
            </Badge>
          );
        },
      },
      {
        header: "Seedpower Points",
        accessorKey: "seedpower_points",
        enableColumnFilter: false,
        cell: (cellProps) => {
          const points = cellProps.getValue();
          return points || <span className="text-muted">0</span>;
        },
      },
      {
        header: "Actions",
        cell: (cellProps) => {
          const employee = cellProps.row.original;
          return (
            <div className="d-flex gap-1">
              <Link
                to={`/employees-detail?userId=${employee.id}`}
                className="btn btn-sm btn-outline-primary"
                title="View Details"
              >
                <i className="ri-eye-line align-bottom"></i>
              </Link>
            </div>
          );
        },
      },
    ],
    []
  );

  // If user doesn't have access
  if (!hasAccess) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Employee Search" pageTitle="Employees" />
          <Row>
            <Col lg={12}>
              <Alert color="warning">
                <h4 className="alert-heading">Access Denied</h4>
                <p>
                  You don't have permission to search employees. Only superadmins and employee3 roles can access this feature.
                </p>
                <hr />
                <p className="mb-0">
                  Your current role: <Badge color="secondary">{userRole}</Badge>
                </p>
              </Alert>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Employee Search" pageTitle="Employees" />
        
        {/* Load initial data button */}
        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader>
                <h4 className="card-title mb-0">Employee Search</h4>
              </CardHeader>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center">
                  <p className="text-muted mb-0">
                    Use the search bar in the table below to find employees. Search works across all employee fields using pattern matching.
                  </p>
                  <Button
                    color="primary"
                    onClick={loadInitialData}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <i className="ri-refresh-line me-1"></i>
                        Load Employees
                      </>
                    )}
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Search Results */}
        {hasSearched && (
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <div className="d-flex align-items-center">
                    <h4 className="card-title mb-0 flex-grow-1">
                      Search Results
                      {totalResults > 0 && (
                        <Badge color="success" className="ms-2">
                          {totalResults} found
                        </Badge>
                      )}
                    </h4>
                    {totalResults > 0 && (
                      <div className="flex-shrink-0">
                        <small className="text-muted">
                          Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalResults)} of {totalResults} results
                        </small>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardBody>
                  {error && (
                    <Alert color="danger">
                      <i className="ri-error-warning-line me-2"></i>
                      {error}
                    </Alert>
                  )}
                  
                  {employees.length > 0 ? (
                    <TableContainer
                      columns={columns}
                      data={employees}
                      total={totalResults}
                      currentPage={currentPage}
                      pageSize={pageSize}
                      onChange={handlePageChange}
                      isGlobalFilter={true}
                      isAddUserList={false}
                      customPageSize={pageSize}
                      className="custom-header-css"
                      divClass="table-responsive table-card mb-2"
                      tableClass="align-middle table-nowrap"
                      theadClass="table-light"
                      SearchPlaceholder="Search employees..."
                    />
                  ) : hasSearched && !loading && (
                    <div className="text-center py-4">
                      <div className="avatar-md mx-auto mb-4">
                        <div className="avatar-title bg-primary-subtle text-primary rounded-circle fs-4">
                          <i className="ri-search-line"></i>
                        </div>
                      </div>
                      <h5>No employees found</h5>
                      <p className="text-muted">
                        No employees match your search criteria. Try adjusting your search terms.
                      </p>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* Initial State */}
        {!hasSearched && !loading && (
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="text-center py-4">
                    <div className="avatar-md mx-auto mb-4">
                      <div className="avatar-title bg-primary-subtle text-primary rounded-circle fs-4">
                        <i className="ri-team-line"></i>
                      </div>
                    </div>
                    <h5>Search for Employees</h5>
                    <p className="text-muted">
                      Enter a search query above to find employees, or leave empty and click "Load All" to see all employees with pagination.
                    </p>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
      <ToastContainer />
    </div>
  );
};

export default EmployeeSearch;